import { useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Fragment } from "react";
import { IoIosCube } from "react-icons/io";
import { Link, useSearchParams } from "react-router-dom";
import { searchByProp } from "utils/search";
import { FETCH_STOCK_SUPPLIERS } from "./graphql";

const SuppliersPage = () => {
  const { loading, error, data } = useQuery(FETCH_STOCK_SUPPLIERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const supplierQ = searchParams.get("supplierQ") || "";

  const setSupplierQ = (nextQ) => {
    const next = new URLSearchParams(searchParams);
    if (nextQ) {
      next.set("supplierQ", nextQ);
    } else {
      next.delete("supplierQ");
    }
    setSearchParams(next);
  };

  const buildToWithSupplierQ = (path) => {
    const next = new URLSearchParams(searchParams);
    if (supplierQ) {
      next.set("supplierQ", supplierQ);
    } else {
      next.delete("supplierQ");
    }
    const queryString = next.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  if (loading) return <Spinner />;
  if (error) return <Errors error={parseError(error)} />;

  const suppliers = data.suppliers
    .map((supplier) => {
      const products = data.products.results.filter((p) => {
        const isDefaultSupplier = p.defaultSupplier?.id === supplier.id;
        const isStockSupplier = p.stockSuppliers?.some(
          (s) => s.id === supplier.id,
        );

        return isDefaultSupplier || isStockSupplier;
      });

      const supplierMatched = searchByProp(
        supplier,
        ["name", "nickName"],
        supplierQ,
      );

      return {
        ...supplier,
        products: supplierMatched
          ? products
          : products.filter((product) =>
              searchByProp(product, ["number", "name"], supplierQ),
            ),
      };
    })
    .filter((i) => i.products.length > 0);

  const duplicatedProducts = suppliers
    .flatMap((supplier) => supplier.products)
    .map((product) => product.id)
    .filter((id, index, arr) => arr.indexOf(id) !== index);

  console.log("duplicatedProducts", duplicatedProducts);

  return (
    <div className="flex-1 overflow-auto flex flex-col p-6">
      <div>
        <SearchBar
          value={supplierQ}
          onChange={setSupplierQ}
          placeholder="Search suppliers or products..."
        />
      </div>

      <div className="p-0 card flex-1 overflow-auto flex flex-col mt-4">
        <div className="flex-1 overflow-auto relative">
          <table>
            <tbody>
              {suppliers.map((supplier) => (
                <Fragment key={supplier.id}>
                  <tr
                    key={supplier.id}
                    className="bg-gray-200 dark:bg-gray-800 sticky top-0"
                  >
                    <td className="font-semibold text-gray-800 dark:text-gray-100 px-4 py-2.5">
                      <h5>{supplier.name}</h5>
                    </td>
                    <td className="text-right px-4">
                      <Link to={buildToWithSupplierQ(`${supplier.id}`)}>
                        Stock Overview
                      </Link>
                    </td>
                  </tr>

                  {supplier.products.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-gray-100 dark:border-gray-700"
                    >
                      <td className="px-4">
                        <Link
                          to={buildToWithSupplierQ(
                            `${supplier.id}/${product.id}`,
                          )}
                        >
                          #{product.id}. [{product.number}] {product.name}
                        </Link>

                        {duplicatedProducts.includes(product.id) && (
                          <span className="ml-2 text-red-600 dark:text-red-400">
                            (Multiple Suppliers)
                          </span>
                        )}
                      </td>
                      <td className=" space-x-1 text-xs text-right">
                        {product.wisStocks ? (
                          <IoIosCube className="text-purple-600/70 inline-block" />
                        ) : null}
                        {product.stockSuppliers.length > 0 ? (
                          <IoIosCube className="text-orange-600/70 inline-block" />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
